import moment from 'jalali-moment';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row, Input, UploadFile as UploadFileType, message } from 'antd';
import { useDevice } from '@/hooks';
import { HelpdeskHttp } from '@/services';
import { Flex, AppButton, AppModal, AppModalProps, UploadFile } from '@/components/common';
import { getUserAssignId, HelpdeskStatus } from '@/components/helpdesk/helpdeskUtils';

export interface HelpdeskAddNoteProps extends AppModalProps {
  issue: RedmineIssueEntity;
  onComplete: () => any;
}

export default function HelpdeskAddNoteModal({
  issue,
  onComplete,
  ...modalProps
}: HelpdeskAddNoteProps) {

  const [t] = useTranslation(['common', 'helpdesk']);
  const [note, setNote] = useState('');
  const { isDesktop } = useDevice();
  const [files, setFiles] = useState<UploadFileType[]>([]);
  const [loading, setLoading] = useState(false);

  const handleAddFile = useCallback((file: UploadFileType) => setFiles(files => [...files, file]), [setFiles]);
  const handleRemove = useCallback((file: UploadFileType) => setFiles(files => files.filter(f => f.uid !== file.uid)), [setFiles]);

  const handleClose = useCallback(() => {
    setNote('');
    setFiles([]);
    setLoading(false);
    modalProps.hide();
  }, [modalProps, setNote, setFiles, setLoading]);

  const handleSubmit = useCallback(async () => {
    const assignId = getUserAssignId(issue);
    setLoading(true);
    const uploads = [];
    for (const file of files) {
      try {
        const response = (await HelpdeskHttp.uploadAttachment(file as any));
        const { upload } = response;
        uploads.push({
          filename: moment().unix() + '-' + file.name,
          token: upload.token,
          content_type: file.type
        });
      }
      catch (e) {
        message.error({ duration: 3, content: t('helpdesk:messages.fail-upload') });
        setLoading(false);
        return;
      }
    }
    try {
      await HelpdeskHttp.updateIssueNotes(issue.id, {
        issue: {
          status_id: HelpdeskStatus.WaitQueue,
          assigned_to_id: assignId,
          notes: note,
          uploads
        },
      });
      message.success({ content: t('helpdesk:messages.success-message') });
      handleClose();
      onComplete();
    }
    catch (e) {
      message.error({ duration: 3, content: t('helpdesk:messages.fail-message') });
    }
  }, [issue, note, files, setLoading, onComplete, handleClose, t]);

  return (
    <AppModal
      width={750}
      footer={null}
      destroyOnHidden
      variant="helpdesk"
      onCancel={handleClose}
      title={t('helpdesk:labels.new-answer')}
      {...modalProps}>
      <Row gutter={[12, 12]}>
        <Col lg={16} span={24}>
          <Flex className="h-full" direction="column">
            <Input.TextArea
              rows={7}
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder={t('helpdesk:labels.answer-text')}
              className="placeholder-app-gray-darker text-app-gray-darker !h-full"
            />
          </Flex>
        </Col>
        <Col lg={8} span={24}>
          <Flex direction="column">
            <UploadFile
              text={
                <span className="text-app-gray">
                  {t('common:messages.choose-your-file')}
                </span>
              }
              style={{ height: 'calc(100% - 120px)' }}
              iconProps={{ style: { color: '#DEDCDC' } }}
              hints={isDesktop() ? undefined : []}
              onChange={handleAddFile as any}
              onRemove={handleRemove as any}
            />
            <AppButton
              size="large"
              type="primary"
              loading={loading}
              appVariant="helpdesk"
              className="font-bold"
              onClick={handleSubmit}
              disabled={note.trim() === ''}
            >
              {t('common:labels.send')}
            </AppButton>
          </Flex>
        </Col>
      </Row>
    </AppModal>
  );
}
